import { useTranslation } from '@/shared/hooks/useTranslation'
import {
  ResourceAdvancedFilter,
  ResourceFilterOptionList,
  ResourceFilterSelect,
  type ResourceFilterOption
} from '@/shared/ui/Layout'

import { getProgrammingKind } from '../model/kind'
import type { FilterOption, ScheduleFilterOptions } from '../model/filterOptions'
import type { AdvancedFilters } from '../model/types'

const EMPTY_FILTERS: AdvancedFilters = {
  place: null,
  speaker: null,
  kind: null
}

type ScheduleAdvancedFilterProps = {
  options: ScheduleFilterOptions
  filters: AdvancedFilters
  hasActiveFilters: boolean
  onPlaceChange: (place: string | null) => void
  onSpeakerChange: (speaker: number | null) => void
  onKindChange: (kind: string | null) => void
  onReset: () => void
  className?: string
}

const hasAnyFilterValue = (filters: AdvancedFilters) =>
  Boolean(filters.place || filters.speaker || filters.kind)

const applyFilters = (
  draft: AdvancedFilters,
  handlers: Pick<
    ScheduleAdvancedFilterProps,
    'onPlaceChange' | 'onSpeakerChange' | 'onKindChange'
  >
) => {
  handlers.onPlaceChange(draft.place)
  handlers.onSpeakerChange(draft.speaker)
  handlers.onKindChange(draft.kind)
}

type ScheduleFilterFieldsProps = {
  layout: 'desktop' | 'mobile'
  emptyOptionLabel: string
  options: ScheduleFilterOptions
  kindOptions: FilterOption<string>[]
  filters: AdvancedFilters
  onPlaceChange: (place: string | null) => void
  onSpeakerChange: (speaker: number | null) => void
  onKindChange: (kind: string | null) => void
  t: (key: string) => string
}

const ScheduleAdvancedFilterFields = ({
  layout,
  emptyOptionLabel,
  options,
  kindOptions,
  filters,
  onPlaceChange,
  onSpeakerChange,
  onKindChange,
  t
}: ScheduleFilterFieldsProps) => {
  if (layout === 'mobile') {
    return (
      <>
        <ResourceFilterOptionList
          label={t('schedule.advancedFilters.place')}
          emptyLabel={emptyOptionLabel}
          options={options.places}
          value={filters.place}
          onChange={onPlaceChange}
        />
        <ResourceFilterOptionList
          label={t('schedule.advancedFilters.speaker')}
          emptyLabel={emptyOptionLabel}
          options={options.speakers}
          value={filters.speaker}
          onChange={onSpeakerChange}
        />
        <ResourceFilterOptionList
          label={t('schedule.advancedFilters.kind')}
          emptyLabel={emptyOptionLabel}
          options={kindOptions}
          value={filters.kind}
          onChange={onKindChange}
        />
      </>
    )
  }

  return (
    <>
      <ResourceFilterSelect
        id="schedule-filter-place"
        label={t('schedule.advancedFilters.place')}
        emptyLabel={emptyOptionLabel}
        options={options.places}
        value={filters.place}
        onChange={onPlaceChange}
      />
      <ResourceFilterSelect
        id="schedule-filter-speaker"
        label={t('schedule.advancedFilters.speaker')}
        emptyLabel={emptyOptionLabel}
        options={options.speakers}
        value={filters.speaker}
        onChange={onSpeakerChange}
      />
      <ResourceFilterSelect
        id="schedule-filter-kind"
        label={t('schedule.advancedFilters.kind')}
        emptyLabel={emptyOptionLabel}
        options={kindOptions}
        value={filters.kind}
        onChange={onKindChange}
      />
    </>
  )
}

export const ScheduleAdvancedFilter = ({
  options,
  filters,
  hasActiveFilters,
  onPlaceChange,
  onSpeakerChange,
  onKindChange,
  onReset,
  className
}: ScheduleAdvancedFilterProps) => {
  const { t } = useTranslation()

  const kindOptions: ResourceFilterOption<string>[] = options.kinds.map(kind => ({
    id: kind.id,
    label: t(getProgrammingKind(kind.id).nameKey)
  }))

  const hasAnyOption =
    options.places.length > 0 ||
    options.speakers.length > 0 ||
    kindOptions.length > 0

  const emptyOptionLabel = t('schedule.advancedFilters.allOption')

  return (
    <ResourceAdvancedFilter
      title={t('schedule.advancedFilters.title')}
      clearLabel={t('schedule.advancedFilters.clearAll')}
      applyLabel={t('schedule.advancedFilters.apply')}
      appliedFilters={filters}
      emptyDraft={EMPTY_FILTERS}
      hasActiveFilters={hasActiveFilters}
      isDraftActive={hasAnyFilterValue}
      hidden={!hasAnyOption}
      className={className}
      triggerAriaLabel={t('schedule.advancedFilters.title')}
      onReset={onReset}
      onApplyDraft={draft =>
        applyFilters(draft, {
          onPlaceChange,
          onSpeakerChange,
          onKindChange
        })
      }
      renderFields={({ layout, filters: currentFilters, updateFilters }) => (
        <ScheduleAdvancedFilterFields
          layout={layout}
          emptyOptionLabel={emptyOptionLabel}
          options={options}
          kindOptions={kindOptions}
          filters={currentFilters}
          onPlaceChange={place => updateFilters({ place })}
          onSpeakerChange={speaker => updateFilters({ speaker })}
          onKindChange={kind => updateFilters({ kind })}
          t={t}
        />
      )}
    />
  )
}
